/**
 * The calendar's shared model (M4 Phase 3): the dot tones, the status-to-tone map,
 * the period shading, and the legend. One tone per meaning, from the existing chip
 * tone family (parts.tsx); the period shading is named in plain words - BLOCK shades
 * the cell in the refusal red's tint, WARN in the gold's.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import "./calendar.css";

import { strings } from "../brand/strings";

export type DotTone =
  | "neutral"
  | "attention"
  | "success"
  | "danger"
  /* Wave W2: where a date stands, the legend's own five. */
  | "draft"
  | "waiting"
  | "agreed"
  | "done"
  | "cancelled";

/** Wave W2: a mark is a planned activity (round) or an RFI (square). */
export type EntryKind = "activity" | "rfi";

/** The period shading a cell can carry, from the constraint_enforcement vocabulary. */
export type Shade = "block" | "warn" | null;

/**
 * The status-to-tone map. The statuses are the activity_status register's tokens;
 * tones come from the same family the state chips use, so a dot and a chip for the
 * same state always agree:
 *   PLANNED - the neutral grey (dates promised, nothing yet said)
 *   CONDUCTED / CLOSED - the teal success
 *   FOLLOW_UP - the gold attention (a conducted record owed a follow-up)
 *   CANCELLED - the refusal red (a date withdrawn, named in the legend's own tone)
 */
const DOT_TONES: Readonly<Record<string, DotTone>> = {
  CONDUCTED: "success",
  CLOSED: "success",
  FOLLOW_UP: "attention",
  RESCHEDULED: "attention",
  PLANNED: "neutral",
  CANCELLED: "danger",
};

export function statusDotTone(status: string): DotTone {
  return DOT_TONES[status] ?? "neutral";
}

export function enforcementShade(enforcement: string): Shade {
  if (enforcement === "BLOCK") {
    return "block";
  }
  if (enforcement === "WARN") {
    return "warn";
  }
  return null;
}

/**
 * ⭐ WAVE EXPERIENCE A: the three states every calendar draws (Mahmoud's doctrine: bright
 * colours are for what needs attention, not for what is already done).
 *   attention - an open act of mine is about the entry (the server's `actionable_by_me`)
 *   open      - everything still in progress that waits on somebody else
 *   over      - done, closed or cancelled: grey
 */
export type AttentionTone = "attention" | "open" | "over";

const OVER_STATUSES: ReadonlySet<string> = new Set(["CONDUCTED", "CLOSED", "CANCELLED"]);

export function activityAttention(entry: {
  readonly negotiationState: string;
  readonly status: string;
  readonly actionableByMe?: boolean | undefined;
}): AttentionTone {
  const where = activityTone(entry);
  if (OVER_STATUSES.has(entry.status) || where === "done" || where === "cancelled") {
    return "over";
  }
  return entry.actionableByMe === true ? "attention" : "open";
}

export function rfiAttention(entry: {
  readonly dateState: string;
  readonly workflowState: string;
  readonly actionableByMe?: boolean | undefined;
}): AttentionTone {
  const where = rfiTone(entry);
  if (where === "done" || where === "cancelled" || entry.workflowState === "CLOSED") {
    return "over";
  }
  return entry.actionableByMe === true ? "attention" : "open";
}

const ATTENTION_TONES: readonly { tone: AttentionTone; label: string }[] = [
  { tone: "attention", label: strings.calendar.legendYours },
  { tone: "open", label: strings.calendar.legendOpen },
  { tone: "over", label: strings.calendar.legendOver },
];

/**
 * THE legend of the calendars since Wave Experience A: the two shapes, the three states the
 * marks are drawn in (exactly these, nothing else), and the two period shadings.
 *
 * `showAttention={false}` (the Director's ruling C, 2026-10-23): a calendar whose feed
 * carries no `actionable_by_me` can never draw "Waiting on you", so its legend does not
 * offer it. The schedule page is the one such calendar today (its activities come from
 * GET /api/schedules/{id}/activities); deferred to wave X through the resolver of ruling A.
 */
export function AttentionLegend({
  showRfis = true,
  showPeriods = true,
  showAttention = true,
}: {
  readonly showRfis?: boolean;
  readonly showPeriods?: boolean;
  readonly showAttention?: boolean;
}): React.JSX.Element {
  return (
    <div className="q-cal__legend" data-testid="attention-legend">
      <span className="q-cal__legend-item q-cal__legend-heading">
        {strings.schedules.legendHeading}
      </span>
      <span className="q-cal__legend-item">
        <span className={dotClass("open")} aria-hidden="true" />
        {strings.calendar.legendActivity}
      </span>
      {showRfis && (
        <span className="q-cal__legend-item">
          <span className={`${dotClass("open")} q-cal__dot--rfi`} aria-hidden="true" />
          {strings.calendar.legendRfi}
        </span>
      )}
      {ATTENTION_TONES.filter((one) => showAttention || one.tone !== "attention").map((one) => (
        <span key={one.tone} className="q-cal__legend-item" data-testid={`legend-${one.tone}`}>
          <span className={dotClass(one.tone)} aria-hidden="true" />
          {one.label}
        </span>
      ))}
      {showPeriods && (
        <>
          <span className="q-cal__legend-item">
            <span className="q-cal__legend-swatch q-cal__legend-swatch--warn" aria-hidden="true" />
            {strings.calendar.shadeWarn}
          </span>
          <span className="q-cal__legend-item">
            <span className="q-cal__legend-swatch q-cal__legend-swatch--block" aria-hidden="true" />
            {strings.calendar.shadeBlock}
          </span>
        </>
      )}
    </div>
  );
}

/** One mark on one day. */
export interface CalendarDot {
  readonly tone: DotTone | AttentionTone;
  readonly label: string;
  /** Round for a planned activity, square for an RFI (wave W2). Round when absent. */
  readonly kind?: EntryKind;
}

/**
 * Wave W2: the tone of a planned activity by where its DATE stands, the question both
 * calendars answer: not yet sent, waiting for an answer, agreed, done, or cancelled.
 */
export function activityTone(entry: {
  readonly negotiationState: string;
  readonly status: string;
}): DotTone {
  if (entry.status === "CANCELLED") {
    return "cancelled";
  }
  switch (entry.negotiationState) {
    case "NOT_DISTRIBUTED":
      return "draft";
    case "AWAITING_CONTRACTOR":
    case "AWAITING_OPERATOR":
      return "waiting";
    case "AGREED":
      return "agreed";
    default:
      return "done";
  }
}

/** Wave W2: the tone of an RFI by where its date stands. */
export function rfiTone(entry: {
  readonly dateState: string;
  readonly workflowState: string;
}): DotTone {
  if (entry.dateState === "CLOSED_WITHOUT_DATE") {
    return "cancelled";
  }
  if (entry.dateState === "AGREED") {
    return entry.workflowState === "CLOSED" ||
      entry.workflowState === "FINAL_APPROVAL" ||
      entry.workflowState === "IN_ACTION"
      ? "done"
      : "agreed";
  }
  if (entry.dateState === "DRAFT") {
    return "draft";
  }
  return "waiting";
}

const PLANNING_TONES: readonly { tone: DotTone; label: string }[] = [
  { tone: "draft", label: strings.calendar.legendDraft },
  { tone: "waiting", label: strings.calendar.legendWaiting },
  { tone: "agreed", label: strings.calendar.legendAgreed },
  { tone: "done", label: strings.calendar.legendDone },
  { tone: "cancelled", label: strings.calendar.legendCancelled },
];

/**
 * Wave W2: THE legend of every planning calendar (the calendar page and every view of a
 * schedule): the two shapes, the five date states, and the two period shadings. Mahmoud
 * read dots with no legend on the schedule page and could not tell what they meant.
 */
export function PlanningLegend({
  showRfis = true,
  showDraft = true,
  showPeriods = true,
}: {
  readonly showRfis?: boolean;
  readonly showDraft?: boolean;
  readonly showPeriods?: boolean;
}): React.JSX.Element {
  return (
    <div className="q-cal__legend" data-testid="planning-legend">
      <span className="q-cal__legend-item q-cal__legend-heading">
        {strings.schedules.legendHeading}
      </span>
      <span className="q-cal__legend-item">
        <span className={dotClass("neutral")} aria-hidden="true" />
        {strings.calendar.legendActivity}
      </span>
      {showRfis && (
        <span className="q-cal__legend-item">
          <span className={`${dotClass("neutral")} q-cal__dot--rfi`} aria-hidden="true" />
          {strings.calendar.legendRfi}
        </span>
      )}
      {PLANNING_TONES.filter((one) => showDraft || one.tone !== "draft").map((one) => (
        <span key={one.tone} className="q-cal__legend-item">
          <span className={dotClass(one.tone)} aria-hidden="true" />
          {one.label}
        </span>
      ))}
      {showPeriods && (
        <>
          <span className="q-cal__legend-item">
            <span className="q-cal__legend-swatch q-cal__legend-swatch--warn" aria-hidden="true" />
            {strings.calendar.shadeWarn}
          </span>
          <span className="q-cal__legend-item">
            <span className="q-cal__legend-swatch q-cal__legend-swatch--block" aria-hidden="true" />
            {strings.calendar.shadeBlock}
          </span>
        </>
      )}
    </div>
  );
}

/** One day as the grids render it. */
export interface CalendarCell {
  readonly iso: string;
  readonly day: number;
  readonly inMonth: boolean;
  readonly dots: readonly CalendarDot[];
  readonly shade: Shade;
}

export function shadeClass(shade: Shade): string {
  if (shade === "block") {
    return " q-cal__cell--block";
  }
  if (shade === "warn") {
    return " q-cal__cell--warn";
  }
  return "";
}

export function dotClass(tone: DotTone | AttentionTone): string {
  return `q-cal__dot q-cal__dot--${tone}`;
}

/** One mark's classes: its tone, and its shape (square for an RFI). */
export function markClass(dot: CalendarDot): string {
  return `${dotClass(dot.tone)}${dot.kind === "rfi" ? " q-cal__dot--rfi" : ""}`;
}

/** The visible legend: the status dots and the two period shadings, in plain words. */
export function CalendarLegend({
  statuses,
}: {
  /** The statuses to name, as [status, label] pairs from the API's answers. */
  statuses: readonly { readonly status: string; readonly label: string }[];
}): React.JSX.Element {
  const seen = new Map<string, string>();
  for (const one of statuses) {
    if (!seen.has(statusDotTone(one.status))) {
      seen.set(statusDotTone(one.status), one.label);
    }
  }
  return (
    <div className="q-cal__legend" data-testid="calendar-legend">
      {[...seen].map(([tone, label]) => (
        <span key={tone} className="q-cal__legend-item">
          <span className={dotClass(tone as DotTone)} aria-hidden="true" />
          {label}
        </span>
      ))}
      <span className="q-cal__legend-item">
        <span className="q-cal__legend-swatch q-cal__legend-swatch--warn" aria-hidden="true" />
        {strings.calendar.shadeWarn}
      </span>
      <span className="q-cal__legend-item">
        <span className="q-cal__legend-swatch q-cal__legend-swatch--block" aria-hidden="true" />
        {strings.calendar.shadeBlock}
      </span>
    </div>
  );
}
